/**
 * Generate JSON-LD structured data for property listings
 * Helps Google understand and index property content better
 */

export const generatePropertySchema = (listing, ownerInfo) => {
  const baseUrl = window.location.origin;
  
  // Property Listing Schema using Product schema
  const propertySchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": listing.title,
    "description": listing.description,
    "image": listing.images && listing.images.length > 0 ? listing.images : [],
    "offers": {
      "@type": "Offer",
      "price": listing.price,
      "priceCurrency": "ZAR",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      "url": `${baseUrl}/marketplace`
    },
    "brand": {
      "@type": "Brand",
      "name": listing.companyName || "Flippy Homes"
    },
    "category": listing.propertyType,
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Property Type",
        "value": listing.propertyType
      },
      {
        "@type": "PropertyValue",
        "name": "Listing Type",
        "value": listing.type === 'sale' ? 'For Sale' : 'For Rent'
      },
      {
        "@type": "PropertyValue",
        "name": "Bedrooms",
        "value": listing.specifications?.beds || 0
      },
      {
        "@type": "PropertyValue",
        "name": "Bathrooms",
        "value": listing.specifications?.baths || 0
      },
      {
        "@type": "PropertyValue",
        "name": "Property Size",
        "value": listing.specifications?.propertySize ? `${listing.specifications.propertySize} sqm` : "N/A"
      }
    ]
  };

  // Add address if available
  if (listing.propertyAddress) {
    propertySchema.address = {
      "@type": "PostalAddress",
      "streetAddress": listing.propertyAddress,
      "addressCountry": "ZA"
    };
  }

  // Real Estate Agent Schema (if owner is an agent)
  const agentSchema = ownerInfo && ownerInfo.userMode === 'Agent' ? {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": ownerInfo.agencyName || ownerInfo.fullName,
    "telephone": listing.contactNumber,
    "email": ownerInfo.email,
    "address": ownerInfo.agencyAddress ? {
      "@type": "PostalAddress",
      "streetAddress": ownerInfo.agencyAddress,
      "addressLocality": ownerInfo.city,
      "postalCode": ownerInfo.postalCode,
      "addressCountry": "ZA"
    } : undefined
  } : null;

  // Local Business Schema (for agency)
  const businessSchema = ownerInfo && ownerInfo.userMode === 'Agent' && ownerInfo.agencyName ? {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": ownerInfo.agencyName,
    "description": `Real estate services by ${ownerInfo.agencyName}`,
    "address": ownerInfo.agencyAddress ? {
      "@type": "PostalAddress",
      "streetAddress": ownerInfo.agencyAddress,
      "addressLocality": ownerInfo.city,
      "postalCode": ownerInfo.postalCode,
      "addressCountry": "ZA"
    } : undefined,
    "telephone": listing.contactNumber,
    "priceRange": "$$"
  } : null;

  // Breadcrumbs Schema
  const breadcrumbsSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Marketplace",
        "item": `${baseUrl}/marketplace`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": listing.title,
        "item": `${baseUrl}/marketplace`
      }
    ]
  };

  // Combine all schemas
  const schemas = [propertySchema, breadcrumbsSchema];
  if (agentSchema) schemas.push(agentSchema);
  if (businessSchema) schemas.push(businessSchema);

  return schemas;
};

export const injectSchema = (schemas) => {
  // Remove existing schema scripts
  const existingScripts = document.querySelectorAll('script[data-schema="property"]');
  existingScripts.forEach(script => script.remove());

  // Inject new schema scripts
  schemas.forEach((schema, index) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'property');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
};

export const removeSchema = () => {
  const existingScripts = document.querySelectorAll('script[data-schema="property"]');
  existingScripts.forEach(script => script.remove());
};
